<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import type { Event } from '@/types'

import { appConfirm } from '@/composables/useAppConfirm'
import { eventService } from '@/services/eventService'
import { EventType } from '@/types'

definePage({
  meta: {
    requiresAuth: true,
  },
})

const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()
const { setBreadcrumbs, buildEventBreadcrumbs } = useBreadcrumbs()
const { setMenuRouteContext } = useLayout()

const event = ref<Event | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
// Get unsaved changes status from child components
const { childHasUnsavedChanges } = useUnsavedChangesCheck()

// An external event member can open the event but not its workspace, so the workspace
// breadcrumbs and the sidebar highlight would both lead to a 403. Drop the workspace
// context for them and let the trail start at the personal event management instead.
const breadcrumbWorkspaceId = computed(() =>
  event.value?.permissions?.externalAccess ? undefined : event.value?.workspaceId,
)

const items = computed(() => {
  if (!event.value) {
    return []
  }
  const permissions = event.value.permissions
  const routes = []

  routes.push({
    route: { name: '/manage/events/[id]/overview', params: { id: event.value.id } },
    label: t('router.pages.events-id-overview'),
    icon: 'i-lucide-chart-bar',
  })
  if (!event.value.isTemplate && permissions?.viewRegistrations) {
    routes.push({
      route: { name: '/manage/events/[id]/registrations', params: { id: event.value.id } },
      label:
        event.value.eventType === EventType.Questionnaire
          ? t('router.pages.events-id-responses')
          : t('router.pages.events-id-registrations'),
      icon: 'i-lucide-book',
    })
  }
  if (
    !event.value.isTemplate &&
    event.value.eventType === EventType.Appointment &&
    permissions?.viewRegistrations
  ) {
    routes.push({
      route: { name: '/manage/events/[id]/planner', params: { id: event.value.id } },
      label: t('router.pages.events-id-planner'),
      icon: 'i-lucide-calendar-range',
    })
  }
  if (!event.value.isTemplate && event.value.questions?.length && permissions?.viewRegistrations) {
    routes.push({
      route: { name: '/manage/events/[id]/analysis', params: { id: event.value.id } },
      label: t('router.pages.events-id-analysis'),
      icon: 'i-lucide-chart-pie',
    })
  }
  if (!event.value.isTemplate && permissions?.exportParticipants) {
    routes.push({
      route: { name: '/manage/events/[id]/export', params: { id: event.value.id } },
      label: t('router.pages.events-id-export'),
      icon: 'i-lucide-file-up',
    })
  }
  if (
    !event.value.isTemplate &&
    event.value.eventType !== EventType.Questionnaire &&
    event.value.workspace?.enableAttendance &&
    permissions?.viewRegistrations
  ) {
    routes.push({
      route: { name: '/manage/events/[id]/attendance', params: { id: event.value.id } },
      label: t('router.pages.events-id-attendance'),
      icon: 'i-lucide-square-check',
    })
  }
  if (
    !event.value.isTemplate &&
    event.value.eventType !== EventType.Questionnaire &&
    event.value.workspace?.enableGrades &&
    permissions?.viewRegistrations
  ) {
    routes.push({
      route: { name: '/manage/events/[id]/grades', params: { id: event.value.id } },
      label: t('router.pages.events-id-grades'),
      icon: 'i-lucide-pencil',
    })
  }
  if (
    permissions?.manageSettings ||
    permissions?.manageUsers ||
    permissions?.manageRules ||
    permissions?.manageSlots ||
    permissions?.manageFees ||
    permissions?.manageGroups
  ) {
    routes.push({
      route: { name: '/manage/events/[id]/settings', params: { id: event.value.id } },
      label: t('router.pages.events-id-settings'),
      icon: 'i-lucide-settings',
    })
  }

  return routes
})

const activeTab = computed(
  () =>
    items.value.find((item) => String(route.name ?? '').includes(item.route.name))?.route.name ||
    '/manage/events/[id]/overview',
)

function getLocalizedEventName(event: Event): string {
  return locale.value === 'de' ? event.nameDE : (event.nameEN ?? event.nameDE)
}

function getLocalizedWorkspaceName(event: Event): string | undefined {
  const workspace = event.workspace
  if (!workspace) {
    return undefined
  }

  return workspace.nameDE
}

// Handle tab click with unsaved changes check
const handleTabClick = async (tabRoute: any) => {
  // If there are unsaved changes, show confirmation dialog
  if (childHasUnsavedChanges.value) {
    const accepted = await appConfirm({
      message: t('pages.events.settings.unsavedChangesMessage'),
      title: t('pages.events.settings.unsavedChangesHeader'),
      confirmColor: 'error',
    })
    if (accepted) await router.push(tabRoute)
  } else {
    // No unsaved changes, navigate directly
    await router.push(tabRoute)
  }
}

async function fetchEvent(id: number) {
  loading.value = true
  error.value = null
  event.value = null
  try {
    event.value = await eventService.getById(id)
    const currentPageTitle =
      route.name === '/manage/events/[id]/overview'
        ? undefined
        : `router.pages.${String(route.name)
            .split('/')
            .filter((x) => x)
            .join('-')
            .replace(/\[/g, '')
            .replace(/\]/g, '')}`

    const breadcrumbItems = buildEventBreadcrumbs(
      event.value.id,
      getLocalizedEventName(event.value),
      currentPageTitle,
      breadcrumbWorkspaceId.value,
      getLocalizedWorkspaceName(event.value),
    )

    setBreadcrumbs(breadcrumbItems)
  } catch (err) {
    console.error(`Error fetching event(${id}):`, err)
    error.value = 'Failed to load event details.'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  const eventId = (route.params as { id?: number }).id
  if (eventId) {
    fetchEvent(eventId)
    if (route.name === '/manage/events/[id]') {
      router.replace({
        name: '/manage/events/[id]/overview',
        params: { id: eventId },
      })
    }
  }
})

onUnmounted(() => {
  setMenuRouteContext(null)
})

watch(
  () => (route.params as { id?: number }).id,
  (newIdParam) => {
    if (newIdParam) {
      fetchEvent(newIdParam)
    }
  },
)
watch(
  () => route.name,
  () => {
    if (event.value) {
      const currentPageTitle =
        route.name === '/manage/events/[id]/overview'
          ? undefined
          : `router.pages.${String(route.name)
              .split('/')
              .filter((x) => x)
              .join('-')
              .replace(/\[/g, '')
              .replace(/\]/g, '')}`

      const breadcrumbItems = buildEventBreadcrumbs(
        event.value.id,
        getLocalizedEventName(event.value),
        currentPageTitle,
        breadcrumbWorkspaceId.value,
        getLocalizedWorkspaceName(event.value),
      )

      setBreadcrumbs(breadcrumbItems)
    }
  },
)
</script>

<template>
  <div class="content-wrap">
    <template v-if="!loading && event">
      <div class="px-1 pt-2 pb-1 md:pt-3 md:pb-2">
        <div class="flex items-center gap-2">
          <UIcon name="i-lucide-book" class="text-xl text-[var(--app-text-primary)] md:text-2xl" />
          <!-- Display name based on locale or fallback -->
          <h1 class="text-lg leading-tight font-bold md:text-xl xl:text-2xl">
            {{ getLocalizedEventName(event) }}
          </h1>
        </div>
      </div>
      <AppTabs :value="activeTab">
        <AppTabList>
          <AppTab
            v-for="tab in items"
            :key="tab.label"
            :value="tab.route.name"
            manual
            @activate="handleTabClick(tab.route)"
          >
            <UIcon :name="tab.icon" />
            <span>{{ tab.label }}</span>
          </AppTab>
        </AppTabList>
      </AppTabs>
      <div class="py-2 md:py-3">
        <router-view :event="event" @reload-event="fetchEvent(event.id)" />
      </div>
    </template>
  </div>
</template>
